import React from 'react';
import ReactDropzone, { DropzoneProps } from 'react-dropzone';

import {
  dropzone,
  dropzoneActiveStyle,
  dropzoneDisabledStyle,
  dropzoneEmptyStyle,
  dropzoneErrorStyle,
  dropzoneFilledStyle,
  dropzoneRejectedStyle,
  Label,
} from 'component/Dropzone/Dropzone.s';
import { DropzoneDefaultMessage } from 'component/Dropzone/DropzoneDefaultMessage';
import isArray from 'lodash/isArray';
import { WrappedFieldProps } from 'redux-form';

import { DropzoneError } from './DropzoneError';

export { DropzoneMessage } from 'component/Dropzone/Dropzone.s';

type ChildrenFunction = (files: File[], isHidden?: boolean) => React.JSX.Element;

type DropzoneChildren = React.ReactNode | ChildrenFunction;

export namespace Dropzone {
  export type Props = Partial<DropzoneProps> & {
    label?: string;
    children?: DropzoneChildren;
    'data-testid'?: string;
  };
}

const isChildrenFunction = (children: DropzoneChildren): children is ChildrenFunction => {
  return typeof children === 'function';
};

const defaultChildrenFunction = (files: File[], isHidden: boolean) => (
  <DropzoneDefaultMessage files={files} isHidden={isHidden} />
);

export const Dropzone: React.FC<Dropzone.Props & WrappedFieldProps> = ({
  label,
  input,
  meta,
  children = defaultChildrenFunction,
  'data-testid': dataTestId,
  ...props
}) => (
  <div data-testid="dropzoneWrapper">
    {label && <Label>{label}</Label>}
    <ReactDropzone
      {...(props as DropzoneProps)}
      style={{
        ...(input.value ? dropzoneFilledStyle : dropzoneEmptyStyle),
        ...(meta.error ? dropzoneErrorStyle : {}),
        ...(props.style || {}),
      }}
      inputProps={{
        // @ts-expect-error
        'data-testid': dataTestId,
        ...props.inputProps,
      }}
      activeStyle={{ ...dropzoneActiveStyle, ...(props.activeStyle || {}) }}
      rejectStyle={{ ...dropzoneRejectedStyle, ...(props.rejectStyle || {}) }}
      disabledStyle={{ ...dropzoneDisabledStyle, ...(props.disabledStyle || {}) }}
      onDropAccepted={(files: File[]) => input.onChange(props.multiple ? files : files[0])}
      className={dropzone() + ' ' + (props.className || '')}
    >
      {isChildrenFunction(children)
        ? children(
            input.value ? (!isArray(input.value) ? [input.value] : input.value) : [],
            Array.isArray(meta.error),
          )
        : children}

      <DropzoneError meta={meta} />
    </ReactDropzone>
  </div>
);
